import ColumnTemplate from '@vindr/ui/src/components/Templates/columns/columnRenderTemplates';
import { useGetAssignsInterval } from '@vindr/services/actions/assign.action';
import { ASSIGNED_STATUS, ASSIGNED_TYPE } from '@vindr/services/constants/assigned.constant';
import { GENDER, STATUS_COLOR } from '@vindr/services/constants/common.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { DefinedColumns } from '@vindr/services/interfaces';
import keycloak from '@vindr/services/keycloak';
import { ActionIcon } from '@vindr/ui';
import { formatDateColumn } from '@vindr/utils/dateTime';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';

interface AssignedColumnsType
  extends DefinedColumns<ReturnType<typeof useGetAssignsInterval>['data']['data'][number]> {}

const useDefinedColumns = ({ onRemove, onEdit, pagination }): AssignedColumnsType[] => {
  const { t } = useTranslation('Vindoc');

  return [
    ColumnTemplate.Index({ pagination }),
    {
      title: t('Order Group'),
      dataIndex: ['order', 'order_group'],
      key: 'order_group',
      width: 110,
      ellipsis: true,
    },
    {
      title: t('Patient ID'),
      width: 110,
      dataIndex: ['order', 'visited_patient', 'patient_id'],
      key: 'visited_patient_id',
      ellipsis: true,
    },
    {
      title: t('Visit ID'),
      width: 100,
      dataIndex: ['order', 'visited_patient', 'visit_id'],
      key: 'visited_patient.visit_id',
      ellipsis: true,
    },
    {
      title: t('Patient Name'),
      width: 200,
      dataIndex: ['order', 'visited_patient', 'name'],
      key: 'visited_patient.name',
      ellipsis: true,
    },
    {
      title: t('Status'),
      width: 90,
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      render: (status) => (
        <Tag
          color={STATUS_COLOR[ASSIGNED_STATUS[status]]}
          className="flex w-full justify-center align-middle"
        >
          {t(ASSIGNED_STATUS[status])}
        </Tag>
      ),
    },
    {
      title: t('Gender'),
      dataIndex: ['order', 'visited_patient', 'sex'],
      key: 'visited_patient.sex',
      width: 70,
      align: 'center',
      render: (sex) => t(GENDER[sex]),
    },
    {
      title: t('Modality'),
      dataIndex: ['order', 'procedure', 'modality'],
      key: 'modality',
      width: 70,
    },
    {
      title: t('Assigned To'),
      dataIndex: 'assign_to',
      key: 'assign_to',
      width: 140,
      ellipsis: true,
      render: (_, record) =>
        record?.type === ASSIGNED_TYPE.USER
          ? (record?.assign_users || []).map((it) => it?.fullname).join(', ')
          : (record?.assign_groups || []).map((it) => it?.name).join(', '),
    },
    {
      title: t('Assigned At'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      sorter: true,
      ellipsis: true,
      defaultSortOrder: 'descend',
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Study Description'),
      dataIndex: ['order', 'procedure', 'compact_name'],
      key: 'study_desc',
      width: 300,
      ellipsis: true,
    },
    {
      title: t('Institution'),
      dataIndex: ['order', 'institution', 'name'],
      key: 'institution.name',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('Actions'),
      dataIndex: 'operation',
      key: 'operation',
      align: 'center',
      width: 80,
      fixed: 'right',
      render: (text, record) => {
        const isCompletedOrReading = [ASSIGNED_STATUS.COMPLETED, ASSIGNED_STATUS.READING].includes(
          record?.status
        );

        return (
          <ActionIcon>
            <ActionIcon.Edit onClick={() => onEdit(record)} disabled={isCompletedOrReading} />
            <ActionIcon.Delete
              onClick={() => onRemove(record)}
              disabled={
                isCompletedOrReading ||
                !keycloak.hasOneOfPerm([PERMISSIONS.assigned.admin_assignedlist])
              }
            />
          </ActionIcon>
        );
      },
    },
  ];
};

export default useDefinedColumns;
